import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { cachedPost } from "../apiCache";
import { fetchCourses, fetchCourseLessonsLite } from "../nativeLearning";
import { norm, score, highlight, readCachedIndex, writeCachedIndex, pooled, INDEX_CONCURRENCY, LESSON_ICON } from "../CourseSearch";
import { toZonedEvent } from "../MembershipViews";
import { asset } from "../assetUrl";

/**
 * Spotlight's search results page — /membership/:token/search?q=…
 *
 * The top-bar search writes the query into the URL; this page reads it and
 * shows every match, grouped: Courses, Lessons, Groups, Events, Members. Only
 * sections whose menu is switched on are searched, so a result never leads to a
 * page the member can't open.
 *
 * Where the data lives decides how each part searches:
 *   courses, groups, events  the lists the app already loads (cached), ranked
 *                            here with the course search's own rules
 *   lessons                  the course search's lesson index (built once per
 *                            tab, shared with the Courses page search)
 *   members                  the members endpoint's own server-side name search
 */

// The same default cover the course cards use.
const DEFAULT_COURSE_IMAGE = asset("/images/course-default.svg");
const LIMIT = { courses: 8, lessons: 10, groups: 8, events: 6, members: 8 };

function Chevron() {
  return (
    <svg className="h-5 w-5 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
    </svg>
  );
}

/** One result: thumbnail, title (matches lit), a meta line, and the way in. */
function ResultRow({ thumb, title, meta, tags = [], onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-2xl border border-gray-200 bg-surface p-3.5 text-left transition hover:border-gray-300 hover:bg-gray-50/60 sm:p-4"
    >
      {thumb}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold text-gray-900 sm:text-[16px]">{title}</span>
        {meta ? <span className="mt-0.5 block truncate text-[13px] text-gray-500">{meta}</span> : null}
        {tags.length ? (
          <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-gray-400">
            {tags.map((t) => <span key={t}>{t}</span>)}
          </span>
        ) : null}
      </span>
      <Chevron />
    </button>
  );
}

function Section({ title, count, loading, children }) {
  return (
    <section className="space-y-2.5" aria-label={title}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[18px] font-semibold tracking-tight text-gray-900">{title}</h3>
        <span className="text-[13px] tabular-nums text-gray-400">{loading ? "…" : count}</span>
      </div>
      {children}
    </section>
  );
}

const RowSkeleton = () => (
  <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-surface p-4">
    <div className="h-16 w-28 shrink-0 animate-pulse rounded-lg bg-gray-100" />
    <div className="flex-1 space-y-2">
      <div className="h-4 w-1/2 animate-pulse rounded bg-gray-100" />
      <div className="h-3 w-1/4 animate-pulse rounded bg-gray-100" />
    </div>
  </div>
);

export default function SpotlightSearchResults({ token, member, primary = "#0274F7", menus = {} }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const q = (sp.get("q") || "").trim();
  const terms = useMemo(() => norm(q).split(" ").filter(Boolean), [q]);
  const on = (key) => Number(menus[key]) !== 1;
  const canCourses = on("course");
  const canGroups = on("community");
  const canEvents = on("event");
  const canMembers = on("member");

  const funnelId = member?.funnel_id;
  const memberId = member?.community_member_id;
  const jwt = member?.member_token;

  // ---- lists (loaded once, ranked locally) ----
  const [courses, setCourses] = useState(null);
  const [groups, setGroups] = useState(null);
  const [events, setEvents] = useState(null);
  const [tz, setTz] = useState("");

  useEffect(() => {
    let alive = true;
    if (canCourses && funnelId && jwt) {
      fetchCourses({ funnelId, memberId, jwt }).then((r) => alive && setCourses(r.courses || [])).catch(() => alive && setCourses([]));
    } else setCourses([]);
    if (canGroups) {
      cachedPost(`${config.COMMUNITY_API_URL}/membership-groups`, { community_token: token, community_member_id: memberId || undefined, list_only: 1 }, { ttl: 30000 })
        .then((r) => alive && setGroups(r.data?.error === 0 ? r.data.groups || [] : []))
        .catch(() => alive && setGroups([]));
    } else setGroups([]);
    if (canEvents) {
      cachedPost(`${config.COMMUNITY_API_URL}/membership-events`, { community_token: token }, { ttl: 30000 })
        .then((r) => {
          if (!alive) return;
          setTz(r.data?.timezone || "");
          setEvents(r.data?.error === 0 ? [...(r.data.upcoming || []), ...(r.data.past || [])] : []);
        })
        .catch(() => alive && setEvents([]));
    } else setEvents([]);
    return () => {
      alive = false;
    };
  }, [token, funnelId, memberId, jwt, canCourses, canGroups, canEvents]);

  // ---- lessons: the shared lesson index, built on first real query ----
  const [lessons, setLessons] = useState(() => (funnelId ? readCachedIndex(funnelId, memberId) : null));
  const [indexing, setIndexing] = useState(false);
  const indexStarted = useRef(false);
  // "Still on the page" for the whole fan-out. Not an effect-local flag: the
  // first partial result changes `lessons`, and a cleanup keyed to that would
  // mark the build dead and drop every course that lands after it.
  // Set on every mount, not just initialised: React's dev double-mount runs the
  // cleanup once before the real mount, and a flag that only ever goes false
  // would drop every result.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const hasIndex = !!lessons;
  const wantsLessons = terms.join("").length >= 2;
  useEffect(() => {
    if (!canCourses || !courses?.length || hasIndex || indexStarted.current || !wantsLessons) return;
    indexStarted.current = true;
    setIndexing(true);
    const acc = [];
    pooled(courses, INDEX_CONCURRENCY, async (c) => {
      let ls = [];
      try {
        ls = await fetchCourseLessonsLite({ funnelId, memberId, jwt, courseToken: c.course_token });
      } catch {
        ls = []; // one bad course shouldn't blank the whole index
      }
      acc.push(...ls.map((l) => ({ ...l, course_name: c.name, course_image: c.image })));
      if (mounted.current) setLessons([...acc]); // stream matches as courses land
    }).finally(() => {
      writeCachedIndex(funnelId, memberId, acc);
      if (mounted.current) setIndexing(false);
    });
  }, [canCourses, courses, hasIndex, wantsLessons, funnelId, memberId, jwt]);

  // ---- members: server-side name search, debounced by the URL itself ----
  const [members, setMembers] = useState(null);
  useEffect(() => {
    if (!canMembers || !q) {
      setMembers([]);
      return undefined;
    }
    const controller = new AbortController();
    setMembers(null);
    const t = setTimeout(() => {
      axios
        .post(`${config.COMMUNITY_API_URL}/membership-members`, { community_token: token, community_member_id: memberId || undefined, q, page: 1, per_page: LIMIT.members }, { signal: controller.signal })
        .then((r) => setMembers(r.data?.error === 0 ? (r.data.members || []).filter((m) => !m.is_self) : []))
        .catch((e) => { if (!axios.isCancel?.(e) && e?.name !== "CanceledError") setMembers([]); });
    }, 200);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [canMembers, q, token, memberId]);

  // ---- rank ----
  const rank = (list, fields, limit) =>
    (list || [])
      .map((x) => ({ x, s: Math.max(...fields.map((f, i) => score(typeof f === "function" ? f(x) : x[f], terms) * (i === 0 ? 1 : 0.6))) }))
      .filter((r) => r.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, limit)
      .map((r) => r.x);

  const found = {
    courses: terms.length ? rank(courses, ["name", "short_description"], LIMIT.courses) : [],
    lessons: terms.length ? rank(lessons, ["title"], LIMIT.lessons) : [],
    groups: terms.length ? rank(groups, ["name", "description"], LIMIT.groups) : [],
    events: terms.length ? rank(events, ["title", "description", "location"], LIMIT.events) : [],
    members: members || [],
  };
  const loading = {
    courses: canCourses && courses === null,
    lessons: canCourses && (courses === null || indexing) && !found.lessons.length,
    groups: canGroups && groups === null,
    events: canEvents && events === null,
    members: canMembers && members === null,
  };
  const total = found.courses.length + found.lessons.length + found.groups.length + found.events.length + found.members.length;
  const busy = Object.values(loading).some(Boolean);
  const across = [canCourses && "courses", canCourses && "lessons", canGroups && "groups", canEvents && "events", canMembers && "members"].filter(Boolean);
  const acrossText = across.length > 1 ? `${across.slice(0, -1).join(", ")} and ${across[across.length - 1]}` : across[0] || "this membership";

  const hl = (text) => highlight(text, terms, primary);
  const go = (path) => navigate(`/membership/${token}/${path}`);
  const courseUrl = (params) => `courses?${new URLSearchParams(params).toString()}`;

  return (
    <div className="w-full">
      {/* On phones the top bar has no search box — this is it. */}
      <form
        className="relative mb-6 lg:hidden"
        onSubmit={(e) => {
          e.preventDefault();
          const v = new FormData(e.currentTarget).get("q")?.toString().trim() || "";
          setSp(v ? { q: v } : {}, { replace: true });
        }}
      >
        <span className="pointer-events-none absolute inset-y-0 start-3.5 flex items-center text-gray-400">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
        </span>
        <input
          type="search"
          name="q"
          enterKeyHint="search"
          defaultValue={q}
          autoFocus={!q}
          placeholder="Search courses, communities, events, members…"
          className="h-11 w-full rounded-xl border bg-surface ps-10 pe-4 text-[14px] text-gray-800 outline-none"
          // Brand-coloured focus (the forms plugin would paint it blue).
          style={{ borderColor: "rgb(var(--c-gray-200))" }}
          onFocus={(e) => { e.target.style.borderColor = primary; e.target.style.boxShadow = `0 0 0 3px ${primary}33`; }}
          onBlur={(e) => { e.target.style.borderColor = "rgb(var(--c-gray-200))"; e.target.style.boxShadow = "none"; }}
        />
      </form>

      <header className="mb-8">
        <h2 className="text-[26px] font-bold tracking-tight text-gray-900 sm:text-[28px]">Search</h2>
        <p className="mt-1 text-[14px] text-gray-500">
          {!q
            ? `Type in the search bar and press Enter to find ${acrossText}.`
            : busy && !total
              ? `Searching for “${q}”…`
              : `${total} ${total === 1 ? "result" : "results"} for “${q}” — across ${acrossText}.`}
        </p>
      </header>

      {!q ? null : !busy && total === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-surface px-6 py-14 text-center">
          <p className="text-[15px] font-semibold text-gray-800">Nothing matches “{q}”</p>
          <p className="mx-auto mt-1 max-w-sm text-[13.5px] text-gray-500">Try a shorter word or check the spelling — search looks at names and titles, not everything inside a lesson.</p>
        </div>
      ) : (
        <div className="space-y-9">
          {canCourses && (loading.courses || found.courses.length > 0) && (
            <Section title="Courses" count={found.courses.length} loading={loading.courses}>
              {loading.courses ? <RowSkeleton /> : found.courses.map((c) => (
                <ResultRow
                  key={c.course_token}
                  onClick={() => go(courseUrl({ course: c.course_token, view: "lessons" }))}
                  thumb={
                    <img
                      src={c.image || DEFAULT_COURSE_IMAGE}
                      onError={(e) => { e.currentTarget.src = DEFAULT_COURSE_IMAGE; }}
                      alt=""
                      className="h-16 w-28 shrink-0 rounded-lg bg-ink object-cover"
                      loading="lazy"
                    />
                  }
                  title={hl(c.name)}
                  meta={c.short_description ? hl(c.short_description) : null}
                  tags={["Course", `${c.total_lessons} ${c.total_lessons === 1 ? "lesson" : "lessons"}`, c.progress_pct > 0 ? `${c.progress_pct}% complete` : null].filter(Boolean)}
                />
              ))}
            </Section>
          )}

          {canCourses && (loading.lessons || found.lessons.length > 0) && (
            <Section title="Lessons" count={found.lessons.length} loading={loading.lessons && !found.lessons.length}>
              {loading.lessons && !found.lessons.length ? <RowSkeleton /> : found.lessons.map((l) => {
                const meta = LESSON_ICON[l.lesson_type] || LESSON_ICON[1];
                return (
                  <ResultRow
                    key={`${l.course_token}:${l.lesson_token}`}
                    onClick={() => go(courseUrl({ course: l.course_token, view: "lessons", lesson: l.lesson_token }))}
                    thumb={
                      <span className="relative h-16 w-28 shrink-0 overflow-hidden rounded-lg bg-ink">
                        {l.course_image ? <img src={l.course_image} alt="" className="h-full w-full object-cover opacity-80" loading="lazy" /> : null}
                        <span className="absolute inset-0 grid place-items-center">
                          <span className="grid h-8 w-8 place-items-center rounded-full bg-black/55 text-white backdrop-blur">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9"><path strokeLinecap="round" strokeLinejoin="round" d={l.locked ? "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" : meta.d} /></svg>
                          </span>
                        </span>
                      </span>
                    }
                    title={hl(l.title)}
                    meta={l.course_name}
                    tags={[meta.label, l.completed ? "Completed" : null, l.locked ? "Locked" : null].filter(Boolean)}
                  />
                );
              })}
            </Section>
          )}

          {canGroups && (loading.groups || found.groups.length > 0) && (
            <Section title="Communities" count={found.groups.length} loading={loading.groups}>
              {loading.groups ? <RowSkeleton /> : found.groups.map((g) => (
                <ResultRow
                  key={g.group_token}
                  // The community page with ?gid opens the group's landing page,
                  // and sends a member who already joined straight into its feed.
                  onClick={() => go(`groups?gid=${encodeURIComponent(g.group_token)}`)}
                  thumb={
                    <span className="h-16 w-28 shrink-0 overflow-hidden rounded-lg bg-ink">
                      {g.banner ? <img src={g.banner} alt="" className="h-full w-full object-cover" loading="lazy" /> : null}
                    </span>
                  }
                  title={hl(g.name)}
                  meta={g.description ? hl(String(g.description).replace(/<[^>]*>/g, " ")) : null}
                  tags={[
                    "Community",
                    Number(g.access_type) === 2 ? "Private" : "Public",
                    g.member_count != null ? `${g.member_count} ${Number(g.member_count) === 1 ? "member" : "members"}` : null,
                    g.joined ? "Joined" : null,
                  ].filter(Boolean)}
                />
              ))}
            </Section>
          )}

          {canEvents && (loading.events || found.events.length > 0) && (
            <Section title="Events" count={found.events.length} loading={loading.events}>
              {loading.events ? <RowSkeleton /> : found.events.map((e) => {
                const z = toZonedEvent(e, tz);
                const [, mo, dd] = String(z.localIso || "").split("-").map(Number);
                return (
                  <ResultRow
                    key={e.id}
                    onClick={() => go("events")}
                    thumb={
                      <span className="grid h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-surface text-center">
                        <span className="block py-0.5 text-[10px] font-bold tracking-[0.06em]" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
                          {mo ? new Date(2000, mo - 1, 1).toLocaleDateString("en-US", { month: "short" }).toUpperCase() : "—"}
                        </span>
                        <span className="grid place-items-center text-[22px] font-extrabold tabular-nums text-gray-900">{dd || "?"}</span>
                      </span>
                    }
                    title={hl(e.title)}
                    meta={[z.dateLabel, z.timeLabel, Number(e.event_type) === 2 ? e.location : "Online"].filter(Boolean).join(" · ")}
                    tags={["Event", e.state === "live" ? "Live now" : e.upcoming ? "Upcoming" : "Past"]}
                  />
                );
              })}
            </Section>
          )}

          {canMembers && (loading.members || found.members.length > 0) && (
            <Section title="Members" count={found.members.length} loading={loading.members}>
              {loading.members ? <RowSkeleton /> : found.members.map((m) => (
                <ResultRow
                  key={m.member_token}
                  onClick={() => go(`members?q=${encodeURIComponent(m.name || "")}`)}
                  thumb={
                    <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full" style={{ backgroundColor: `${primary}1f`, color: primary }}>
                      {m.img ? (
                        <img src={m.img} alt="" className="h-full w-full object-cover" loading="lazy" />
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" /></svg>
                      )}
                    </span>
                  }
                  title={hl(m.name || "Member")}
                  meta={[m.location, m.about ? String(m.about).slice(0, 90) : null].filter(Boolean).join(" · ") || null}
                  tags={["Member", m.is_admin ? "Admin" : null, m.points ? `${m.points} pts` : null].filter(Boolean)}
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
